import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Character } from '@/entities/character/types/character'
import { pb, logWriteError, newRecordId, isRecordId, type CharacterRecord, type Json } from '@/shared/api'
import { useAuthStore } from '@/shared/session'
import { useCharacterStore } from './characterStore'
import { newShareId, shareUrl } from '../lib/sharing'
import { migrateCharacterKeys, migrateMetaKeys } from '../lib/migrate'
import { uploadAvatarIfNeeded, removeAvatar, isHostedAvatar } from '../lib/avatarUpload'

export interface SavedCharacterMeta {
  name: string
  playerName: string
  level: number
  className: string
  speciesName: string
}

export interface SavedCharacter {
  id: string
  character: Character
  savedAt: number
  meta?: SavedCharacterMeta
  /** Public share id, set while the character is shared via a capability URL. */
  shareId?: string
  /** Kept as a quick-access shortcut on the Faces list. Local to this browser, not synced to the cloud. */
  pinned?: boolean
  /** When this character was last opened (not merely edited). Local to this browser, not synced to the cloud. */
  lastOpenedAt?: number
}

interface SavedCharactersStore {
  characters: SavedCharacter[]
  activeId: string | null
  /**
   * Ids of characters known to exist in the cloud. Populated by `loadFromCloud`
   * and kept current as writes succeed. Not persisted, because it reflects this
   * session's knowledge of the server, so it starts empty until a cloud load.
   */
  cloudIds: string[]
  saveCurrent: (character: Character, meta?: SavedCharacterMeta) => string
  /** Write the latest character payload to localStorage. The cloud write is debounced via scheduleCloudSync. */
  syncActive: (character: Character) => void
  /** Debounce a cloud write. Multiple calls within delayMs collapse into one. */
  scheduleCloudSync: (delayMs: number) => void
  remove: (id: string) => void
  /**
   * Copy a character under a fresh id, named "<name> (Copy)". Returns the new
   * id, or '' when the source isn't found. Never carries over the original's
   * share id — a copy is never pre-shared — and never touches `activeId`.
   */
  duplicate: (id: string) => string
  /** Toggle a character's quick-access pin. No-op for an unknown id. */
  togglePinned: (id: string) => void
  /** Stamp a character as opened now (not merely saved). No-op for an unknown id. */
  touchOpened: (id: string) => void
  setActive: (id: string | null) => void
  loadFromCloud: () => Promise<void>
  /** Forget cloud-membership knowledge (e.g. on sign-out). Local characters stay. */
  clearCloudIds: () => void
  /**
   * On opening a cloud-saved character, move an inline/non-hosted portrait into the
   * portraits collection (128×128 @90% JPEG) and rewrite the avatar to the hosted URL —
   * locally and in the cloud row. A no-op when the character isn't cloud-saved or
   * its avatar already comes from the file store.
   */
  hostActiveAvatarIfNeeded: (id: string) => Promise<void>
  /**
   * Publish a character behind a share URL (reusing its id if already shared).
   * Requires sign-in. Returns the share URL, or null on failure.
   */
  shareCharacter: (id: string) => Promise<string | null>
  /** Revoke a character's share URL. */
  unshareCharacter: (id: string) => Promise<void>
}

/**
 * A new character's id, in PocketBase's own shape.
 *
 * The id is minted here, offline, because localStorage is the source of truth: a
 * character has an id as soon as it is saved, long before any request. Matching
 * PocketBase's shape is what lets this BE the record id, so every cloud write is
 * a single call rather than a lookup followed by a create or a patch. That is
 * what stands in for `upsert`, which PocketBase does not have.
 */
const newId = newRecordId

function currentUserId(): string | null {
  return useAuthStore.getState().user?.id ?? null
}

const characters = () => pb.collection('characters')

/** The row body for a character, as the collection's fields are named. */
const rowFor = (entry: SavedCharacter, character: Character, userId: string) => ({
  user: userId,
  data: character as unknown as Json,
  meta: (entry.meta ?? null) as unknown as Json | null,
  saved_at: entry.savedAt,
})

/**
 * Write a character, creating the record if it is not there yet.
 *
 * The `upsert` replacement, in one place. Update first because that is the
 * common case by a wide margin, so the create is the exception rather than a
 * lookup every save pays for.
 */
async function put(id: string, body: Record<string, unknown>): Promise<void> {
  try {
    await characters().update(id, body)
  } catch (error) {
    if ((error as { status?: number }).status !== 404) throw error
    await characters().create({ id, ...body })
  }
}

/** A cloud row as this store holds it. Pinning and "recently opened" are local-only, so a cloud row starts without either. */
const toSaved = (row: CharacterRecord): SavedCharacter => ({
  id: row.id,
  // Cloud rows are raw JSON written by any past build; normalize legacy keys.
  character: migrateCharacterKeys(row.data as unknown as Character),
  savedAt: row.saved_at,
  meta: migrateMetaKeys((row.meta as unknown as SavedCharacterMeta | null) ?? undefined),
  // PocketBase has no NULL for text: an unshared character carries ''.
  shareId: row.share || undefined,
  pinned: false,
})

let cloudSyncTimer: ReturnType<typeof setTimeout> | null = null

export const useSavedCharactersStore = create<SavedCharactersStore>()(
  persist(
    (set, get) => {
      /** Record that one or more characters now exist in the cloud. */
      const markCloudSynced = (...ids: string[]) =>
        set((s) => {
          const next = new Set(s.cloudIds)
          for (const id of ids) next.add(id)
          return { cloudIds: [...next] }
        })

      /**
       * Prepare a character for the JSON field: replace an inline base64 portrait
       * with a hosted URL (resized 128×128 @90%). On the first upload, rewrite
       * the portrait to the URL in both this store and the live wizard character so
       * later syncs see a plain URL and skip re-uploading the same image.
       */
      const hostAvatar = async (id: string, character: Character, userId: string, force = false): Promise<Character> => {
        const hosted = await uploadAvatarIfNeeded(character, userId, id, force)
        if (hosted.avatar !== character.avatar) {
          set((s) => ({
            characters: s.characters.map((c) =>
              c.id === id ? { ...c, character: { ...c.character, avatar: hosted.avatar } } : c,
            ),
          }))
          const wizard = useCharacterStore.getState()
          if (wizard.character.avatar === character.avatar) wizard.update({ avatar: hosted.avatar })
        }
        return hosted
      }

      return {
      characters: [],
      activeId: null,
      cloudIds: [],

      saveCurrent: (character, meta) => {
        const { activeId, characters: existing } = get()
        const updating = !!activeId && existing.some((c) => c.id === activeId)
        const id = updating ? activeId! : newId()
        const pinned = updating ? (existing.find((c) => c.id === id)?.pinned ?? false) : false
        const lastOpenedAt = updating ? existing.find((c) => c.id === id)?.lastOpenedAt : undefined
        const entry: SavedCharacter = { id, character, savedAt: Date.now(), meta, pinned, lastOpenedAt }
        set({
          characters: updating
            ? existing.map((c) => (c.id === id ? entry : c))
            : [...existing, entry],
          activeId: id,
        })
        const userId = currentUserId()
        if (userId) {
          void (async () => {
            const hosted = await hostAvatar(entry.id, entry.character, userId)
            await put(entry.id, rowFor(entry, hosted, userId))
            markCloudSynced(entry.id)
          })().catch(logWriteError('save character'))
        }
        return id
      },

      syncActive: (character) => {
        const { activeId, characters: existing } = get()
        if (!activeId || !existing.some((c) => c.id === activeId)) return
        const savedAt = Date.now()
        set({
          characters: existing.map((c) =>
            c.id === activeId ? { ...c, character, savedAt } : c,
          ),
        })
        // The cloud write is handled by scheduleCloudSync.
      },

      scheduleCloudSync: (delayMs) => {
        if (!currentUserId()) return
        if (cloudSyncTimer) clearTimeout(cloudSyncTimer)
        cloudSyncTimer = setTimeout(() => {
          void (async () => {
            const userId = currentUserId()
            if (!userId) return
            const { activeId, characters: existing } = get()
            if (!activeId) return
            const entry = existing.find((c) => c.id === activeId)
            if (!entry) return
            const hosted = await hostAvatar(entry.id, entry.character, userId)
            await put(entry.id, rowFor(entry, hosted, userId))
            markCloudSynced(activeId)
          })().catch(logWriteError('sync character'))
        }, delayMs)
      },

      remove: (id) => {
        set((state) => ({
          characters: state.characters.filter((c) => c.id !== id),
          activeId: state.activeId === id ? null : state.activeId,
          cloudIds: state.cloudIds.filter((cid) => cid !== id),
        }))
        if (currentUserId()) {
          void characters().delete(id).catch((error: unknown) => {
            // Already gone is the answer for a character that never reached the
            // cloud, which is an ordinary thing to delete.
            if ((error as { status?: number }).status !== 404) {
              logWriteError('delete character')(error)
            }
          })
          // Drop the portrait too, so nothing belonging to the character lingers.
          void removeAvatar(id)
        }
      },

      duplicate: (id) => {
        const { characters: existing } = get()
        const source = existing.find((c) => c.id === id)
        if (!source) return ''
        const newIdVal = newId()
        const character: Character = {
          ...source.character,
          name: source.character.name ? `${source.character.name} (Copy)` : source.character.name,
        }
        const entry: SavedCharacter = { id: newIdVal, character, savedAt: Date.now(), meta: source.meta, pinned: false }
        set({ characters: [...existing, entry] })
        const userId = currentUserId()
        if (userId) {
          void (async () => {
            // Forced: a hosted avatar must get its own record, not the original's.
            const hosted = await hostAvatar(entry.id, entry.character, userId, true)
            await put(entry.id, rowFor(entry, hosted, userId))
            markCloudSynced(entry.id)
          })().catch(logWriteError('duplicate character'))
        }
        return newIdVal
      },

      togglePinned: (id) =>
        set((s) => ({
          characters: s.characters.map((c) => (c.id === id ? { ...c, pinned: !c.pinned } : c)),
        })),

      touchOpened: (id) =>
        set((s) => ({
          characters: s.characters.map((c) => (c.id === id ? { ...c, lastOpenedAt: Date.now() } : c)),
        })),

      setActive: (id) => set({ activeId: id }),

      loadFromCloud: async () => {
        const userId = currentUserId()
        if (!userId) return

        let rows: CharacterRecord[]
        try {
          rows = await characters().getFullList<CharacterRecord>({
            // Own characters only. The rules would also hand back a co-member's
            // shared character, which belongs on the party screen and not in
            // somebody's own list of faces.
            filter: pb.filter('user = {:user}', { user: userId }),
            sort: '-saved_at',
          })
        } catch (error) {
          logWriteError('load characters')(error)
          return
        }

        const cloudById = new Map(rows.map((row) => [row.id, toSaved(row)]))

        const byId = new Map<string, SavedCharacter>()
        for (const local of get().characters) byId.set(local.id, local)
        for (const [id, cloud] of cloudById) {
          const existing = byId.get(id)
          if (!existing || cloud.savedAt > existing.savedAt) {
            // Pinning and "recently opened" are local-only: keep this browser's own values
            // even when the cloud's character data is newer and otherwise wins.
            byId.set(id, existing ? { ...cloud, pinned: existing.pinned, lastOpenedAt: existing.lastOpenedAt } : cloud)
          }
        }

        const merged = [...byId.values()].sort((a, b) => b.savedAt - a.savedAt)
        set({ characters: merged, cloudIds: [...cloudById.keys()] })

        const toUpload = merged.filter((entry) => {
          const cloud = cloudById.get(entry.id)
          return !cloud || entry.savedAt > cloud.savedAt
        })
        if (toUpload.length > 0) {
          void (async () => {
            // One request each, where Supabase took a single bulk upsert.
            // PocketBase has no batch write in the records API, and this only
            // ever runs on sign-in for characters the browser has that the
            // server does not.
            for (const entry of toUpload) {
              const hosted = await hostAvatar(entry.id, entry.character, userId)
              await put(entry.id, rowFor(entry, hosted, userId))
              markCloudSynced(entry.id)
            }
          })().catch(logWriteError('upload characters'))
        }
      },

      clearCloudIds: () => set({ cloudIds: [] }),

      hostActiveAvatarIfNeeded: async (id) => {
        const userId = currentUserId()
        if (!userId) return
        const { characters: existing, cloudIds } = get()
        // Only migrate characters we know live in the cloud; a browser-only entry
        // has nowhere to move its portrait to.
        if (!cloudIds.includes(id)) return
        const entry = existing.find((c) => c.id === id)
        const avatar = entry?.character.avatar
        // Already hosted or no portrait at all → do nothing.
        if (!entry || !avatar || isHostedAvatar(avatar)) return
        const hosted = await hostAvatar(entry.id, entry.character, userId)
        // Upload failed (hostAvatar already logged); the avatar is unchanged.
        if (hosted.avatar === avatar) return
        // Persist the hosted URL so the inline image leaves the cloud JSON field and
        // later opens see a hosted URL. Don't bump saved_at: this isn't a user edit.
        await characters()
          .update(id, { data: hosted as unknown as Json })
          .catch(logWriteError('host avatar'))
      },

      shareCharacter: async (id) => {
        const userId = currentUserId()
        const entry = get().characters.find((c) => c.id === id)
        if (!userId || !entry) return null
        const shareId = entry.shareId ?? newShareId()
        try {
          const hosted = await hostAvatar(entry.id, entry.character, userId)
          // The whole row, so sharing also works for a character that hasn't
          // reached the cloud yet (created offline, or a write failed earlier).
          await put(entry.id, { ...rowFor(entry, hosted, userId), share: shareId })
        } catch (error) {
          logWriteError('share character')(error)
          return null
        }
        set((s) => ({
          characters: s.characters.map((c) => (c.id === id ? { ...c, shareId } : c)),
          cloudIds: s.cloudIds.includes(id) ? s.cloudIds : [...s.cloudIds, id],
        }))
        return shareUrl(shareId)
      },

      unshareCharacter: async (id) => {
        if (!currentUserId()) return
        try {
          // '' and not null: PocketBase text fields have no NULL, and the unique
          // index on `share` is partial on `share != ''` so any number of
          // characters may be unshared at once.
          await characters().update(id, { share: '' })
        } catch (error) {
          logWriteError('unshare character')(error)
          return
        }
        set((s) => ({
          characters: s.characters.map((c) => (c.id === id ? { ...c, shareId: undefined } : c)),
        }))
      },
      }
    },
    {
      name: 'dnd-saved-characters',
      version: 3,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ characters: state.characters }),
      // v1 saves predate the race → species rename (raceId/subraceId/raceName).
      // v2 saves carry UUID ids, from when the cloud was Postgres and the column
      // was typed `uuid`. PocketBase will not accept one, so they are re-ided
      // here. Safe by construction: a v2 save has never been near PocketBase, so
      // no server row and no party membership can be pointing at the old id.
      migrate: (persisted) => {
        const state = persisted as { characters?: SavedCharacter[] }
        return {
          ...state,
          characters: (state.characters ?? []).map((entry) => ({
            ...entry,
            id: isRecordId(entry.id) ? entry.id : newId(),
            character: migrateCharacterKeys(entry.character),
            meta: migrateMetaKeys(entry.meta),
          })),
        }
      },
      // `migrate` only runs on a version mismatch, and the character's shape changes
      // without this store's version changing (multiclassing did), so every load
      // brings its characters up to date. A no-op for a current `schemaVersion`.
      merge: (persisted, current) => ({
        ...current,
        characters: ((persisted as { characters?: SavedCharacter[] } | undefined)?.characters ?? []).map((entry) => ({
          ...entry,
          character: migrateCharacterKeys(entry.character),
          meta: migrateMetaKeys(entry.meta),
          // A save from before pinning/recent tracking carries neither field.
          pinned: entry.pinned ?? false,
        })),
      }),
    },
  ),
)
